<template>
    <div>
        <!-- Overview -->
        <div class="card">
            <div class="card-header border-bottom d-flex justify-content-between align-items-center pr-2">
                <h5 class="header-case mb-0">{{ __("Overview") }}</h5>
                <a :href="exportLink" class="btn btn-primary">{{ __('Download report') }}</a>
            </div>
            <div class="card-body" :class="{ loading: statsLoading }">
                <div class="loader">
                    <i class="fas fa-circle-notch fa-spin"></i> Loading
                    sentiment data...
                </div>

                <div class="row" v-if="currentBreakdownStats">
                    <div class="col-md-4 col-12 text-md-left text-center">
                        <div class="sent-label">
                            <strong class="light-text">{{ __('Total Mentions') }}</strong>
                        </div>
                        <span class="metric-number">
                            {{
                                sentimentStats.overall.positive +
                                sentimentStats.overall.negative
                            }}
                        </span>
                        <span class="light-text-small" v-if="
                            sentimentStats.overall.positive &&
                            sentimentStats.overall.negative &&
                            sentimentStats.overall.positive +
                            sentimentStats.overall.negative !=
                            sentimentStats.overall.comparison.positive +
                            sentimentStats.overall.comparison.negative
                        ">
                            <span v-if="
                                parseInt(sentimentStats.overall.positive) + parseInt(sentimentStats.overall.negative) >
                                parseInt(sentimentStats.overall.comparison.positive) + parseInt(sentimentStats.overall.comparison.negative)
                            ">
                                <i class="ei-arrow_up"></i>+ {{
                                    Math.abs(
                                        (parseInt(sentimentStats.overall.positive) + parseInt(sentimentStats.overall.negative)) -
                                        (parseInt(sentimentStats.overall.comparison.positive) + parseInt(sentimentStats.overall.comparison.negative))
                                    )
                                }}
                            </span>
                            <span v-else>
                                <i class="ei-arrow_down"></i>- {{
                                    Math.abs(
                                        (parseInt(sentimentStats.overall.positive) + parseInt(sentimentStats.overall.negative)) -
                                        (parseInt(sentimentStats.overall.comparison.positive) + parseInt(sentimentStats.overall.comparison.negative))
                                    )
                                }}
                            </span>
                        </span>
                    </div>
                    <div class="col-md-4 col-6 text-md-left text-center mt-3 mt-md-0">
                        <div class="sent-label">
                            <div class="face face-lite d-inline-block mr-1 success"></div>
                            <strong class="light-text">{{ __('Positive') }}</strong>
                        </div>
                        <span class="metric-number ml-2">
                            {{ sentimentStats.overall.positive }}
                        </span>
                        <span class="light-text-small" v-if="
                            sentimentStats.overall.positive &&
                            sentimentStats.overall.comparison.positive &&
                            sentimentStats.overall.positive !=
                            sentimentStats.overall.comparison.positive
                        ">
                            <span v-if="
                                parseInt(sentimentStats.overall.positive) >
                                parseInt(sentimentStats.overall.comparison.positive)
                            ">
                                <i class="ei-arrow_up"></i>+ {{
                                    Math.abs(parseInt(sentimentStats.overall.positive) - parseInt(sentimentStats.overall.comparison.positive))
                                }}
                            </span>
                            <span v-else>
                                <i class="ei-arrow_down"></i>- {{
                                    Math.abs(parseInt(sentimentStats.overall.positive) - parseInt(sentimentStats.overall.comparison.positive))
                                }}
                            </span>
                        </span>
                    </div>
                    <div class="col-md-4 col-6 text-md-left text-center mt-3 mt-md-0">
                        <div class="sent-label">
                            <div class="face face-lite d-inline-block mr-1 danger"></div>
                            <strong class="light-text">{{ __('Negative') }}</strong>
                        </div>
                        <span class="metric-number ml-2">
                            {{ sentimentStats.overall.negative }}
                        </span>
                        <span class="light-text-small" v-if="
                            sentimentStats.overall.negative &&
                            sentimentStats.overall.comparison.negative &&
                            sentimentStats.overall.negative !=
                            sentimentStats.overall.comparison.negative
                        ">
                            <span v-if="
                                parseInt(sentimentStats.overall.negative) >
                                parseInt(sentimentStats.overall.comparison.negative)
                            ">
                                <i class="ei-arrow_up"></i>+ {{
                                    Math.abs(
                                        parseInt(sentimentStats.overall.negative) -
                                        parseInt(sentimentStats.overall.comparison.negative)
                                    )
                                }}
                            </span>
                            <span v-else>
                                <i class="ei-arrow_down"></i>- {{
                                    Math.abs(
                                        parseInt(sentimentStats.overall.negative) -
                                        parseInt(sentimentStats.overall.comparison.negative)
                                    )
                                }}
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Sources Card -->
        <div class="card" v-if="sourceStats && sourceStats.length">
            <div class="card-header">
                <h5 class="header-case mb-0">
                    <span>{{ __('Sources') }}</span>
                </h5>
            </div>
            <div class="card-body pb-0" :class="{ loading: sourcesLoading }">
                <div class="loader">
                    <i class="fas fa-circle-notch fa-spin"></i>
                    {{ __('Loading source insights...') }}
                </div>
                <div v-if="sourceStats.length">
                    <div class="row border-bottom group-row align-items-center px-2 py-3 py-md-0" v-for="source in sourceStats"
                        :key="source.name" :class="{ 'selected-source': activeSource === source.name }">
                        <!-- Name & Score -->
                        <div class="col-md-4 col-12 justify-content-center pl-0">
                            <div class="d-flex py-2 px-2 justify-content-md-between justify-content-center align-items-center group-name cursor pointer"
                                @click.prevent="toggleSource(source)">
                                <strong class="mb-0">
                                    <div class="gradient-pill fixed mr-2" :class="'gradient-pill-' + sourceScoreClass(source)">
                                        <span :class="'gradient-' + sourceScoreClass(source)">
                                            {{ scorePct(source) }}%
                                        </span>
                                    </div>
                                    {{ formatSourceName(source.name) || __('Unknown') }}
                                </strong>
                                <span class="see-more"><i class="fa fa-arrow-right"></i></span>
                            </div>
                        </div>

                        <!-- Positive column -->
                        <div class="col-md-2 col-4 hover-halo p-2 cursor-pointer d-flex flex-lg-row flex-md-column align-items-center justify-content-center"
                            @click="toggleSource(source)">
                            <div>
                                <div class="face face-lite d-inline-block align-middle mr-1 success"></div>
                                <span class="text-success f1">{{ source.data.positive }}</span>
                            </div>
                            <span class="light-text-small ml-2"
                                v-if="source.comparisonData && source.comparisonData.positive !== undefined && source.data.positive !== source.comparisonData.positive">
                                <span>
                                    <span v-if="parseInt(source.data.positive) > parseInt(source.comparisonData.positive)" class="ei-arrow_up">+</span>
                                    <span v-else-if="parseInt(source.data.positive) < parseInt(source.comparisonData.positive)" class="ei-arrow_down">-</span>
                                    {{ Math.abs(source.data.positive - source.comparisonData.positive) }}
                                </span>
                            </span>
                        </div>

                        <!-- Negative column -->
                        <div class="col-md-2 col-7 hover-halo p-2 d-flex flex-lg-row flex-md-column justify-content-md-start justify-content-end align-items-center"
                            @click="toggleSource(source)">
                            <div>
                                <div class="face face-lite d-inline-block align-middle mr-1 danger"></div>
                                <span class="text-danger f1">{{ source.data.negative }}</span>
                            </div>
                            <span class="light-text-small ml-2"
                                v-if="source.comparisonData && source.comparisonData.negative !== undefined && source.data.negative !== source.comparisonData.negative">
                                <span v-if="parseInt(source.data.negative) > parseInt(source.comparisonData.negative)">
                                    <span class="ei-arrow_up">+</span>
                                </span>
                                <span v-else-if="parseInt(source.data.negative) < parseInt(source.comparisonData.negative)"
                                    class="ei-arrow_down">-</span>
                                {{ Math.abs(source.data.negative - source.comparisonData.negative) }}
                            </span>
                        </div>

                        <!-- Share & Rating -->
                        <div class="d-flex p-0 flex-md-column flex-xl-row col-md-4 justify-content-between align-items-center">
                            <div>
                                <span class="badge badge-primary cursor-pointer d-inline-block p-2"
                                    @click="toggleSource(source)">
                                    {{ sharePercent(source).toFixed(1) }}% {{ __('Share') }}
                                </span>
                            </div>
                            <div>
                                <span class="badge badge-info cursor-pointer d-inline-block p-2" @click="toggleSource(source)">
                                    {{ rating(source).toFixed(2) }}/5
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Categories -->
        <div class="card">
            <div class="card-header">
                <h5 class="header-case mb-0"><span>{{ __('Categories') }}</span></h5>
            </div>
            <div class="card-body pb-0" v-if="groupStats" :class="{ loading: groupsLoading }">
                <div class="loader">
                    <i class="fas fa-circle-notch fa-spin"></i> Loading
                    sentiment groups...
                </div>
                <div>
                    <div class="row border-bottom group-row align-items-center px-2 py-3 py-md-0"
                        v-for="group in groupStats" :key="group.name">
                        <div class="col-md-4 col-12 justify-content-center pl-0">
                            <div class="d-flex py-2 px-2 justify-content-md-between justify-content-center align-items-center group-name cursor pointer"
                                @click.prevent="expandGroup(group)">
                                <strong class="mb-0 text-uppercase">
                                    <div class="gradient-pill fixed mr-2" :class="'gradient-pill-' + scoreClass(group)
                                        ">
                                        <span :class="'gradient-' + scoreClass(group)
                                            ">{{ groupScore(group) }}%</span>
                                    </div>
                                    {{ __(group.name) }}
                                </strong>
                                <span class="see-more"><i class="fa fa-arrow-right"></i></span>
                            </div>
                        </div>
                        <div class="col-md-2 col-4 hover-halo p-2 cursor-pointer d-flex flex-lg-row flex-md-column align-items-center justify-content-center"
                            @click="goToGroupReviews(group.name, 'positive')">
                            <div>
                                <div class="face face-lite d-inline-block align-middle mr-1 success"></div>
                                <span class="text-success f1">{{
                                    group.data.positive
                                    }}</span>
                            </div>
                            <span class="light-text-small" v-if="
                                group.comparisonData.positive &&
                                group.comparisonData.positive !=
                                group.data.positive
                            ">
                                <span v-if="
                                    parseInt(group.data.positive) >
                                    parseInt(
                                        group.comparisonData.positive
                                    )
                                ">
                                    <i class="ei-arrow_up"></i>+</span>
                                <i v-else-if="
                                    parseInt(group.data.positive) <
                                    parseInt(
                                        group.comparisonData.positive
                                    )
                                " class="ei-arrow_down">-</i>
                                {{
                                    parseInt(group.comparisonData.positive)
                                }}
                            </span>
                        </div>
                        <div class="col-md-2 col-7 hover-halo p-2 d-flex flex-lg-row flex-md-column justify-content-md-start justify-content-end align-items-center"
                            @click="goToGroupReviews(group.name, 'negative')">
                            <div>
                                <div class="face face-lite d-inline-block align-middle mr-1 danger"></div>
                                <span class="text-danger f1">{{
                                    group.data.negative
                                    }}</span>
                            </div>
                            <span class="light-text-small" v-if="
                                group.comparisonData.negative &&
                                group.comparisonData.negative !=
                                group.data.negative
                            ">
                                <span v-if="
                                    parseInt(group.data.negative) >
                                    parseInt(group.comparisonData.negative)
                                ">
                                    <i class="ei-arrow_up"></i>+{{
                                        parseInt(group.data.negative) -
                                        parseInt(group.comparisonData.negative)
                                    }}
                                </span>
                                <span v-else-if="
                                    parseInt(group.data.negative) <
                                    parseInt(group.comparisonData.negative)
                                ">
                                    <i class="ei-arrow_down"></i>-{{
                                        parseInt(group.comparisonData.negative) -
                                        parseInt(group.data.negative)
                                    }}
                                </span>
                            </span>
                        </div>
                        <div
                            class="d-flex p-0 flex-md-column flex-xl-row col-md-4 justify-content-between align-items-center">
                            <div>
                                <span class="badge badge-success cursor-pointer d-inline-block p-2"
                                    v-if="group.data.topPositiveConcept" @click="
                                        goToReviews(
                                            group.data.topPositiveConcept,
                                            'positive'
                                        )
                                        ">
                                    {{
                                        __(
                                            labelizeGeneral(
                                                group.data.topPositiveConcept.subcategory.replace(
                                                    "_general",
                                                    ""
                                                )
                                            )
                                        )
                                    }}
                                </span>
                            </div>
                            <div>
                                <span class="badge badge-danger cursor-pointer d-inline-block p-2"
                                    v-if="group.data.topNegativeConcept" @click="
                                        goToReviews(
                                            group.data.topNegativeConcept,
                                            'negative'
                                        )
                                        ">
                                    {{
                                        __(
                                            labelizeGeneral(
                                                group.data.topNegativeConcept.subcategory.replace(
                                                    "_general",
                                                    ""
                                                )
                                            )
                                        )
                                    }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- Breakdown -->
        <div class="card" id="all-topics">
            <div class="card-header pr-3">
                <div class="d-flex flex-md-row flex-column justify-content-md-between">
                    <div class="my-auto">
                        <strong>{{ __('Breakdown') }}</strong>
                    </div>
                    <div class="input-and-submit-wrapper mt-md-0 mt-3">
                        <input type="text" class="form-control text-center" name="group-name" id="group-name"
                            v-model="topicName" placeholder="Search" />
                    </div>
                    <div class="mt-md-0 mt-4">
                        <strong class="mr-md-1 mr-3">{{ __('Select topic') }}</strong>
                        <select v-model="activeGroup">
                            <option :value="null">{{ __('All topics') }}</option>
                            <option :value="group" v-for="group in groupStats" :key="group.name">{{ __(group.name) }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
            <div class="card-body" style="max-height: 400px; overflow-y: auto; overflow-x: hidden;">
                <div v-if="currentBreakdownStats" :class="{ loading: statsLoading }">
                    <div class="loader">
                        <i class="fas fa-circle-notch fa-spin"></i> {{ __('Loading sentiment data...') }}
                    </div>
                    <div class="header-row row border-bottom">
                        <div class="col-md-6 col-3 mt-md-0 mt-3">
                            <span class="light-text cursor-pointer" @click="toggleSort('topic')">
                                {{ __('Topic') }}
                                <i class="fa" :class="{
                                    'fa-arrow-up': sortDirection == 'asc',
                                    'fa-arrow-down': sortDirection == 'desc'
                                }" v-if="activeSort == 'topic'"></i>
                            </span>
                        </div>
                        <div class="col text-center mt-md-0 mt-3 ml-md-0 ml-3">
                            <span class="light-text cursor-pointer" @click="toggleSort('total')">
                                {{ __('Total') }}
                                <i class="fa" :class="{
                                    'fa-arrow-up': sortDirection == 'asc',
                                    'fa-arrow-down': sortDirection == 'desc'
                                }" v-if="activeSort == 'total'"></i>
                            </span>
                        </div>
                        <div class="col text-center">
                            <div class="cursor-pointer" @click="toggleSort('positive')">
                                <div class="face face-lite d-inline-block align-middle mr-1 success"></div>
                                <span class="light-text">
                                    {{ __('Positive') }}
                                    <i class="fa" :class="{
                                        'fa-arrow-up':
                                            sortDirection == 'asc',
                                        'fa-arrow-down':
                                            sortDirection == 'desc'
                                    }" v-if="activeSort == 'positive'"></i>
                                </span>
                            </div>
                        </div>
                        <div class="col text-center">
                            <div class="cursor-pointer" @click="toggleSort('negative')">
                                <div class="face face-lite d-inline-block align-middle mr-1 danger"></div>
                                <span class="light-text">
                                    {{ __('Negative') }}
                                    <i class="fa" :class="{
                                        'fa-arrow-up':
                                            sortDirection == 'asc',
                                        'fa-arrow-down':
                                            sortDirection == 'desc'
                                    }" v-if="activeSort == 'negative'"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="sentiment-row border-bottom" v-for="topic of currentBreakdownStats">
                        <div class="row align-items-center py-1" :class="{ 'row-active': expand == topic }">
                            <div class="col-md-6 col-4">
                                {{
                                    __(
                                        labelizeGeneral(
                                            topic.subcategory.replace(
                                                "_general",
                                                ""
                                            )
                                        )
                                    )
                                }}
                            </div>
                            <div class="col-md-2 col-2 text-center">
                                <span>{{
                                    parseInt(topic.positive) +
                                    parseInt(topic.negative)
                                }}</span>
                            </div>
                            <div class="col-md-2 col-3 text-center stats-col hover-halo cursor-pointer p-2"
                                @click="goToReviews(topic, 'positive')">
                                <span class="text-success f-600">{{
                                    topic.positive
                                    }}</span>
                                <span class="light-text-small d-flex" v-if="
                                    topic.comparison &&
                                    topic.comparison.positive !=
                                    topic.positive
                                ">
                                    <span v-if="
                                        parseInt(topic.positive) >
                                        parseInt(topic.comparison.positive)
                                    ">
                                        <i class="ei-arrow_up"></i>+{{
                                            parseInt(topic.positive) -
                                            parseInt(topic.comparison.positive)
                                        }}
                                    </span>
                                    <span v-else-if="
                                        parseInt(topic.positive) <
                                        parseInt(topic.comparison.positive)
                                    ">
                                        <i class="ei-arrow_down"></i>-{{
                                            parseInt(topic.comparison.positive) -
                                            parseInt(topic.positive)
                                        }}
                                    </span>
                                </span>
                            </div>
                            <div class="col-md-2 col-3 text-center stats-col hover-halo cursor-pointer p-2"
                                @click="goToReviews(topic, 'negative')">
                                <span class="text-danger f-600">{{
                                    topic.negative
                                    }}</span>
                                <span class="light-text-small d-flex" v-if="
                                    topic.comparison &&
                                    topic.comparison.negative !=
                                    topic.negative
                                ">
                                    <span v-if="
                                        parseInt(topic.negative) >
                                        parseInt(
                                            topic.comparison.negative
                                        )
                                    ">
                                        <i class="ei-arrow_up"></i>+</span>
                                    <span v-else-if="
                                        parseInt(topic.negative) <
                                        parseInt(
                                            topic.comparison.negative
                                        )
                                    ">
                                        <i class="ei-arrow_down"></i>-</span>
                                    {{
                                        parseInt(topic.negative) -
                                        parseInt(topic.comparison.negative)
                                    }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BarChart from "../charts/bar-chart";
export default {
    components: {
        BarChart
    },
    computed: {
        totalSourceReviews() {
            if (!this.sourceStats) return 0;
            return this.sourceStats.reduce((sum, s) => sum + (s.data.total || (s.data.positive + s.data.negative)), 0);
        },
        currentBreakdownStats() {
            var stats = null;
            if (!this.sentimentStats) {
                return null;
            }
            if (this.activeGroup == null) {
                stats = this.sentimentStats.topics;
            } else {
                stats = this.activeGroup.data.topics;
            }

            return stats.sort((a, b) => {
                const key = this.activeSort;
                a.positive = parseInt(a.positive);
                a.negative = parseInt(a.negative);
                a.total = a.positive + a.negative;
                b.total = b.positive + b.negative;
                const aValue = key == "topic" ? a.subcategory : a[key];
                const bValue = key == "topic" ? b.subcategory : b[key];
                if (aValue > bValue) {
                    return this.sortDirection == "desc" ? -1 : 1;
                }
                if (aValue < bValue) {
                    return this.sortDirection == "desc" ? 1 : -1;
                }
                return 0;
            }).filter((element) => {
                if (this.topicName == "") {
                    return true;
                }

                return element.subcategory.toLowerCase().includes(this.topicName.toLowerCase());
            });
        },
        exportLink() {
            let url = "/report/hotel/" +
                Spark.state.currentTeam.id +
                "/adhoc?comparePreviousType=previous_period&fromDate=" +
                this.params.fromDate +
                "&toDate=" +
                this.params.toDate +
                "&comparisonFromDate=" +
                this.params.comparisonFromDate +
                "&comparisonToDate=" +
                this.params.comparisonToDate +
                "&selectedReports[]=sentimentAnalysis&report_type=xls";
            
            // Add source parameter if a source is selected
            if (this.activeSource) {
                url += "&source=" + encodeURIComponent(this.activeSource);
            }
            
            return url;
        },
        sentimentChartData() {
            return {
                datasets: [
                    {
                        backgroundColor: "#ff8068",
                        label: "Negative",
                        data: this.groupTimeseries.map(element => {
                            return element.negative;
                        })
                    },
                    {
                        backgroundColor: "#91e084",
                        label: "Positive",
                        data: this.groupTimeseries.map(element => {
                            return element.positive;
                        })
                    }
                ],
                labels: this.groupTimeseries.map(element => {
                    return element.period;
                })
            };
        },
        activeGroupPositiveTopics() {
            if (!this.activeGroupTopics) return [];
            return this.activeGroupTopics
                .filter(element => {
                    return element.positive > 0;
                })
                .map(element => {
                    return {
                        mentions: element.positive,
                        subcategory: element.subcategory.replace("_general", "")
                    };
                });
        },
        activeGroupNegativeTopics() {
            if (!this.activeGroupTopics) return [];
            return this.activeGroupTopics
                .filter(element => {
                    return element.negative > 0;
                })
                .map(element => {
                    return {
                        mentions: element.negative,
                        subcategory: element.subcategory.replace("_general", "")
                    };
                });
        }
    },
    data() {
        return {
            topicName: "",
            searchQuery: "",
            sentimentStats: null,
            groupStats: null,
            sourceStats: null,
            expand: "",
            activeGroup: null,
            activeSource: null, 
            sourceFiltering: false,
            groupsLoading: false,
            statsLoading: false,
            sourcesLoading: false,
            groupTimeseries: null,
            activeSort: "total",
            sortDirection: "desc",
            groupChartOptions: {
                legend: {
                    display: false
                },
                maintainAspectRatio: false,
                responsive: true,
                scales: {
                    xAxes: [
                        {
                            stacked: true,
                            maxBarThickness: 30,
                            gridLines: {
                                display: false
                            }
                        }
                    ],
                    yAxes: [
                        {
                            stacked: true,
                            ticks: {
                                beginAtZero: true
                            },
                            gridLines: {
                                display: false
                            }
                        }
                    ]
                }
            }
        };
    },
    props: ["params"],
    watch: {
        params: {
            immediate: true,
            deep: true,
            handler: function (newVal, oldVal) {
                this.getAllStats();
                this.getGroupStats();
                this.getSourceStats();
                if (this.activeGroup) {
                    // this.getActiveGroupTimeseries();
                }
            }
        },
        activeSource: {
            handler: function() {
                this.refreshStatsForSource();
            }
        }
    },
    methods: {
    toggleSource(source) {
      if (source && source.name) {
        this.activeSource = (this.activeSource === source.name) ? null : source.name;
      }
    },

    refreshStatsForSource() {
      const hasSource = !!this.activeSource;
      const base      = {
        fromDate:            this.params.fromDate,
        toDate:              this.params.toDate,
        comparisonFromDate:  this.params.comparisonFromDate,
        comparisonToDate:    this.params.comparisonToDate,
      };
      const qs = hasSource ? { ...base, source: this.activeSource } : base;

      this.statsLoading = true;
      axios.get(`/api/terms/${Spark.state.currentTeam.id}/stats`, { params: qs })
           .then(r => {
             this.sentimentStats = r.data;
             this.statsLoading = false;
           })
           .catch(err => {
             console.error('Error loading stats:', err);
             this.statsLoading = false;
           });

      this.groupsLoading = true;
      axios.get(`/api/terms/${Spark.state.currentTeam.id}/group-stats`, { params: qs })
           .then(r => {
             this.groupStats = r.data;
             this.groupsLoading = false;
           })
           .catch(err => {
             console.error('Error loading group stats:', err);
             this.groupsLoading = false;
           });
    },
        scorePct(entity) {
            const total = entity.data.positive + entity.data.negative;
            return total ? ((entity.data.positive / total) * 100).toFixed(0) : 0;
        },
        sourceScoreClass(entity) {
            const pct = this.scorePct(entity);
            return pct >= 80 ? "success" : pct >= 60 ? "warning" : "danger";
        },
        rating(source) {
            const total = source.data.positive + source.data.negative;
            return total ? (source.data.positive / total) * 5 : 0; // out of 5
        },
        sharePercent(source) {
            return this.totalSourceReviews ? (source.data.total / this.totalSourceReviews) * 100 : 0;
        },
        getSourceStats() {
            this.sourcesLoading = true;
            const apiPrefix = window.location.pathname.startsWith("/iframe") ? "/iframe" : "";
            axios
                .get(`${apiPrefix}/api/terms/${Spark.state.currentTeam.id}/source-stats`, {
                    params: {
                        fromDate: this.params.fromDate,
                        toDate: this.params.toDate,
                        comparisonFromDate: this.params.comparisonFromDate,
                        comparisonToDate: this.params.comparisonToDate,
                    },
                })
                .then((r) => {
                    // ensure each source has total if backend didn’t send it
                    this.sourceStats = r.data.map((src) => {
                        src.data.total = src.data.total || src.data.positive + src.data.negative;
                        return src;
                    });
                })
                .catch((err) => console.error("source-stats", err))
                .then(() => {
                    this.sourcesLoading = false;
                });
        },
        goToSourceReviews(sourceName, type) {
            this.$eventHub.$emit("applyFilters", {
                reviewSource: sourceName,
                positiveNegativeOrBoth: type,
                fromDate: this.params.fromDate,
                toDate: this.params.toDate,
            });
            $(
                '.spark-settings-tabs a[href$="#reviews"]',
            ).tab("show");
        },
        toggleSort(sort) {
            if (this.activeSort == sort) {
                this.sortDirection =
                    this.sortDirection == "asc" ? "desc" : "asc";
                return;
            }
            this.activeSort = sort;
            this.sortDirection = "desc";
        },
        scoreClass(group) {
            const score = this.groupScore(group);
            if (score == 0) {
                return "";
            }
            if (score >= 80) {
                return "success";
            }
            if (score >= 60) {
                return "warning";
            }
            return "danger";
        },
        groupScore(group) {
            const sum = group.data.positive + group.data.negative;
            if (sum == 0) {
                return 0;
            }
            const ratio = group.data.positive / sum;
            return (ratio * 100).toFixed(0);
        },
        goToReviews(topic, type) {
            let data = {
                terms: "",
                fromDate: this.params.fromDate,
                toDate: this.params.toDate,
                positiveNegativeOrBoth: type
            };
            if (topic.subcategory) {
                data.subcategory = topic.subcategory;
            }
            if (!data.keyword && !data.subcategory) {
                return;
            }
            this.$eventHub.$emit("applyFilters", data);
            $('.spark-settings-tabs a[href$="#reviews"]').tab("show");
        },
        goToGroupReviews(group, type) {
            let data = {
                terms: "",
                fromDate: this.params.fromDate,
                toDate: this.params.toDate,
                sentimentGroup: group,
                positiveNegativeOrBoth: type
            };
            this.$eventHub.$emit("applyFilters", data);
            $('.spark-settings-tabs a[href$="#reviews"]').tab("show");
        },
        getGroupStats() {
            this.groupsLoading = true;
            axios
                .get(
                    `/api/terms/${Spark.state.currentTeam.id}/group-stats?fromDate=${this.params.fromDate}
                        &toDate=${this.params.toDate}
                        &comparisonFromDate=${this.params.comparisonFromDate}
                        &comparisonToDate=${this.params.comparisonToDate}`
                )
                .then(response => {
                    this.groupsLoading = false;
                    this.groupStats = response.data;
                });
        },
        getAllStats() {
            this.statsLoading = true;
            axios
                .get(
                    `/api/terms/${Spark.state.currentTeam.id}/stats?fromDate=${this.params.fromDate}
                        &toDate=${this.params.toDate}
                        &comparisonFromDate=${this.params.comparisonFromDate}
                        &comparisonToDate=${this.params.comparisonToDate}`
                )
                .then(response => {
                    this.statsLoading = false;
                    this.sentimentStats = response.data;
                });
        },
        expandTopic(topic) {
            if (this.expand == topic) {
                this.expand = "";
                return;
            }
            this.expand = topic;
        },
        getActiveGroupTimeseries() {
            const paramsStr = `fromDate=${this.params.fromDate}&toDate=${this.params.toDate
                }&group=${this.activeGroup.name}`;
            this.groupTimeseries = null;
            axios
                .get(
                    `/api/terms/${Spark.state.currentTeam.id
                    }/group-timeseries?${paramsStr}`
                )
                .then(response => {
                    this.groupTimeseries = response.data;
                });
        },
        getActiveGroupTopics() {
            const paramsStr = `fromDate=${this.params.fromDate}&toDate=${this.params.toDate
                }&group=${this.activeGroup.name}`;
            this.activeGroupTopics = null;
            axios
                .get(
                    `/api/terms/${Spark.state.currentTeam.id
                    }/group-breakdown?${paramsStr}`
                )
                .then(response => {
                    this.activeGroupTopics = response.data;
                });
        },
        expandGroup(group) {
            this.activeGroup = group;
            // this.getActiveGroupTimeseries();

            window.scroll({
                behavior: "smooth",
                left: 0,
                top:
                    document
                        .getElementById("all-topics")
                        .getBoundingClientRect().top + window.pageYOffset
            });
        },
        hideGroup() {
            this.activeGroup = null;
        },
        formatSourceName(sourceName) {
            if (!sourceName) return 'Unknown';

            return sourceName
                .split('_')
                .map(word => word.toLowerCase() === 'com' ? 'Com' : word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
                .replace(' Com', '.com');
        }
    }
};
</script>
<style scoped>
.sent-label {
	height: 30px;
}

.face {
	line-height: initial !important;
	height: auto !important;
	width: auto !important;
}

.row-active {
	margin-bottom: 10px;
	border-bottom: solid 1px #e9eaec;
	box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.19);
}

.group-row .group-name .see-more {
	display: none;
	text-decoration: none;
}

.group-row .group-name:hover {
	background: rgba(0, 0, 0, 0.04);
}

.group-row:hover .group-name .see-more {
	display: block;
}

.badge {
	width: 115px;
	border-radius: 12px;
	color: #333;
	font-weight: 600;
	font-size: 12px;
}

.badge:hover {
	box-shadow: 0 0 2px rgba(0, 0, 0, 0.49);
}

.badge-success {
	background: #b8eeaf;
}

.badge-danger {
	background: #ffbeb2;
}

.stats-col {
	position: relative;
}

.stats-col .light-text-small {
	position: absolute;
	top: 50%;
	left: calc(50% + 25px);
	transform: translate(-50%, -50%);
}

.badge-primary {
	background: #e6f7ff;
	color: #0076ce;
}

.badge-info {
	background: #e3f2fd;
	color: #197fd2;
}

.gradient-pill {
	border-radius: 12px;
	display: inline-block;
	padding: 2px 8px;
	font-size: 13px;
	min-width: 55px;
	text-align: center;
}

 .selected-source {
   background-color: rgba(0,123,255,.12) !important;
   box-shadow: 0 0 6px rgba(0,123,255,.5) !important;
   border-radius: 4px;
   transition: all 0.2s ease;
 }

.gradient-pill-success {
	background-color: rgba(145, 224, 132, 0.2);
}

.gradient-pill-warning {
	background-color: rgba(255, 193, 7, 0.2);
}

.gradient-pill-danger {
	background-color: rgba(255, 128, 104, 0.2);
}

.gradient-success {
	color: #28a745;
}

.gradient-warning {
	color: #ffc107;
}

.gradient-danger {
	color: #dc3545;
}
</style>
